import type { WorkflowState } from "@/lib/types";

/**
 * Stacked horizontal bar of documents by workflow_state plus a small
 * legend underneath. Designed to fit a single dashboard card.
 *
 * Pure HTML/CSS — no SVG library. The colour palette matches the
 * WorkflowBadge tones used elsewhere so the chart reads cohesively.
 */

const STATES: { value: WorkflowState; label: string; color: string }[] = [
  { value: "published",          label: "Published",      color: "#10b981" }, // emerald-500
  { value: "private_only",       label: "Staff portal",   color: "#3b82f6" }, // blue-500
  { value: "pending_review",     label: "Pending dir.",   color: "#f59e0b" }, // amber-500
  { value: "pending_ceo_review", label: "Pending CEO",    color: "#8b5cf6" }, // violet-500
  { value: "owner_only",         label: "Private vault",  color: "#64748b" }, // slate-500
  { value: "rejected",           label: "Rejected",       color: "#ef4444" }, // red-500
];

interface WorkflowBarsProps {
  data: Partial<Record<WorkflowState, number>> | undefined;
  loading?: boolean;
}

export function WorkflowBars({ data, loading }: WorkflowBarsProps) {
  const rows  = STATES.map((s) => ({ ...s, n: data?.[s.value] ?? 0 }));
  const total = rows.reduce((s, r) => s + r.n, 0);

  return (
    <div className="space-y-3">
      {/* Stacked bar */}
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
        {!loading && total > 0 && rows.filter((r) => r.n > 0).map((r) => (
          <div
            key={r.value}
            className="h-full transition-[width] duration-300"
            style={{ width: `${(r.n / total) * 100}%`, backgroundColor: r.color }}
            title={`${r.label}: ${r.n}`}
          />
        ))}
      </div>

      {/* Legend / numbers */}
      <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        {rows.map((r) => {
          const pct = total > 0 ? Math.round((r.n / total) * 100) : 0;
          return (
            <li key={r.value} className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ backgroundColor: r.color }}
                aria-hidden
              />
              <span className="flex-1 truncate text-muted-foreground">{r.label}</span>
              <span className="font-mono tabular-nums text-foreground">{r.n}</span>
              <span className="w-8 text-right font-mono tabular-nums text-muted-foreground">
                {pct}%
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
